// The one workflow-state vocabulary, shared by all four boards (DCTL-2257):
// the SDE project board, the QC dashboard, the manager/project-management board
// and the completed-extractions table. Before this each board carried its own
// local map, and the same hex meant different states on different boards -
// #5c6bc0 was "assigned, not started" on one and "Ready for QC" on another, the
// beginning and the end of one stage.
//
// Seven states over FOUR hue buckets - see design-system/COLOR.md, "REV 2.1 -
// the workflow axis, ruled again" and "REV 2.1.1 - `assigned` is queued". Hue
// answers "does this need a human, and is anything wrong?", not "which stage is
// this?": queued (neutral) / in flight (orange) / rework (rust) / complete
// (green). The stage itself is carried by column position plus the glyph below,
// which is mandatory - hue is never the only channel (COLOR.md governing rule 3,
// WCAG 1.4.1).
//
// Every entry names a STATE, never a bucket. The bucketing lives in the token
// files (scss/_tokens.scss, mirrored into the Vuetify theme in
// plugins/vuetify.js), so a re-ruling is a change there and no call-site sweep
// here. Do not put hex values in this file.
//
// Rework is the only state allowed a 4px left card edge, because it is the only
// state with negative valence AND a required action (see KanbanCard.vue).

// The design system's triplet for one state token: `color` for fills and the
// 4px edge, `ink` for text and icons (>=4.5:1 on white and on its own tint),
// `tint` for the opaque 10% wash. All three are live Vuetify theme keys, so
// each works as a `color="..."` prop or as a `bg-*` / `text-*` utility class.
//
// Pair them as `bg-<tint> text-<ink>` for a chip or tile rather than reaching
// for variant="tonal": tonal composites an alpha wash, and COLOR.md rule 4
// rules tints opaque. Vuetify emits every `.bg-*` rule ahead of every `.text-*`
// rule, so the ink class reliably wins the colour both classes set.
const triplet = (token) => ({
	color: token,
	ink: `${token}-ink`,
	tint: `${token}-tint`,
});

export const WORKFLOW_STATES = {
	// --- Bucket QUEUED: no work has happened yet ---------------------------
	available: {
		label: "Available",
		icon: "mdi-inbox-arrow-down-outline",
		...triplet("state-available"),
	},
	// The SDE board's "assigned" (claimed, not yet started) is a seventh state
	// REV 2.1 never enumerated, and implementing the shared map first put it in
	// flight on the reasoning that a curator does hold it. 2.1.1 ruled the other
	// way: custody is not this axis's job - that is the whole basis for In QC
	// sharing orange with In progress below, and if "a named human holds this"
	// were enough to leave queued then Ready for QC would have kept its own hue
	// too and the four-bucket collapse unravels. The test is whether work is
	// happening, not whose name is on it.
	//
	// The key/label mismatch is deliberate: the key names the transition, which
	// is what a wire protocol should do, and the label names the outstanding
	// work, which is what a curator scans for. Do not "tidy" it.
	assigned: {
		label: "Not started",
		icon: "mdi-account-arrow-right-outline",
		...triplet("state-assigned"),
	},
	// "Ready for QC" is the reviewer's Available - the same state seen from the
	// other side. 2.1 took it off Dactyl blue, which was an incidental borrow.
	ready: {
		label: "Ready for QC",
		icon: "mdi-clipboard-check-outline",
		...triplet("state-ready"),
	},

	// --- Bucket IN FLIGHT: work is actively happening ----------------------
	progress: {
		label: "In progress",
		icon: "mdi-progress-pencil",
		...triplet("state-progress"),
	},
	// 2.1 folded In QC into the in-flight orange: plum was a real semantic tie
	// to the reviewer's provenance domain, but no user can see both axes at
	// once, so it was not worth a hue.
	inQc: {
		label: "In QC",
		icon: "mdi-progress-wrench",
		...triplet("state-in-qc"),
	},

	// --- Bucket REWORK: the only state with negative valence ---------------
	// Rust, not orange, and always this glyph. Hue here is doing salience, not
	// identification - a curator scans a board dozens of times a day and this
	// is the card they most need to catch peripherally.
	rework: {
		label: "Rework",
		icon: "mdi-arrow-u-left-top",
		...triplet("state-rework"),
	},

	// --- Bucket COMPLETE: terminal, no action ------------------------------
	complete: {
		label: "Complete",
		icon: "mdi-check-circle-outline",
		...triplet("state-complete"),
	},
};

// For a column or stat tile that is not on the workflow axis at all (e.g. a
// "Total Papers" total). Neutral chrome, and no glyph, because there is no
// state to discriminate.
export const NEUTRAL_TOKENS = {
	color: "fg-2",
	ink: "fg-2",
	tint: "surface-recessed",
};

// What a COLUMN names in place of a state key when it is off the axis - a
// merged column whose members span more than one bucket (DCTL-2279). It is
// deliberately not a key of WORKFLOW_STATES, so it resolves through the neutral
// fallback below: every entry in that map names a real state, and this names the
// absence of one. Say this rather than omitting `state`, so the neutral reads as
// the decision it is and not as a forgotten field.
export const NEUTRAL_STATE = "neutral";

// Resolve a state key to its triplet + label + glyph, falling back to neutral
// chrome so an unknown or absent key renders instead of throwing.
export function workflowState(key) {
	return WORKFLOW_STATES[key] || NEUTRAL_TOKENS;
}
